import { convertToPln, generateDueDates, addMonths, todayIso } from '@apka/core'
import type { IsoDate } from '@apka/core'
import { biezacyIdUzytkownika } from './api.js'
import { db } from './db.js'
import { zakolejkuj } from './kolejka.js'
import type { Dostawca, Kategoria, Klient, Platnosc, Pozycja } from './typy.js'

/** Ile miesięcy harmonogramu rysujemy lokalnie, zanim serwer wygeneruje swój. */
const HORYZONT_MIESIECY = 12

function teraz(): string {
  return new Date().toISOString()
}

export interface DaneZaplaty {
  actualAmountMinor?: number
  actualCurrency?: string
  paidOn?: IsoDate
  notes?: string
}

/**
 * Odhacza płatność. Kwotę w złotówkach liczymy tym samym kodem co serwer,
 * więc wynik na ekranie jest ostateczny — także wtedy, gdy telefon nie ma
 * zasięgu i operacja poczeka w kolejce.
 */
export async function oznaczZaplacone(id: string, dane: DaneZaplaty = {}): Promise<void> {
  const przed = await db.payments.get(id)
  if (przed === undefined) throw new Error('Nie znaleziono płatności')

  const dzien = dane.paidOn ?? todayIso()
  const kwota = dane.actualAmountMinor ?? przed.plannedAmountMinor
  const waluta = dane.actualCurrency ?? przed.plannedCurrency
  const kursy = await db.fxRates.toArray()
  const przeliczone = convertToPln(kwota, waluta, dzien, kursy)

  const po: Platnosc = {
    ...przed,
    status: 'paid',
    actualAmountMinor: kwota,
    actualCurrency: waluta,
    fxRate: przeliczone === null ? null : String(przeliczone.rate),
    amountPlnMinor: przeliczone?.amountPlnMinor ?? null,
    paidAt: `${dzien}T12:00:00.000Z`,
    notes: dane.notes ?? przed.notes,
    updatedAt: teraz(),
  }
  await db.payments.put(po)

  await zakolejkuj({
    metoda: 'POST',
    sciezka: `/api/payments/${id}/pay`,
    // Pozycja i termin pozwalają serwerowi rozpoznać płatność, nawet gdy
    // odhaczono ją na lokalnym szkicu harmonogramu.
    body: { ...dane, paidOn: dzien, itemId: przed.itemId, dueDate: przed.dueDate },
    cofniecie: [{ tabela: 'payments', id, rekord: przed }],
    skutek: [{ tabela: 'payments', id, rekord: po }],
  })
}

async function ustawStatus(id: string, akcja: 'unpay' | 'skip'): Promise<void> {
  const przed = await db.payments.get(id)
  if (przed === undefined) throw new Error('Nie znaleziono płatności')

  const po: Platnosc = {
    ...przed,
    status: akcja === 'skip' ? 'skipped' : 'planned',
    actualAmountMinor: null,
    actualCurrency: null,
    fxRate: null,
    amountPlnMinor: null,
    paidAt: null,
    paidBy: null,
    updatedAt: teraz(),
  }
  await db.payments.put(po)

  await zakolejkuj({
    metoda: 'POST',
    sciezka: `/api/payments/${id}/${akcja}`,
    body: { itemId: przed.itemId, dueDate: przed.dueDate },
    cofniecie: [{ tabela: 'payments', id, rekord: przed }],
    skutek: [{ tabela: 'payments', id, rekord: po }],
  })
}

export const cofnijZaplate = (id: string) => ustawStatus(id, 'unpay')
export const pomin = (id: string) => ustawStatus(id, 'skip')

export type DanePozycji = Omit<
  Pozycja,
  'createdAt' | 'updatedAt' | 'deletedAt' | 'createdBy' | 'id'
>

/**
 * Rysuje harmonogram pozycji lokalnie tą samą funkcją, której użyje serwer.
 * Dzięki temu nowy koszt wchodzi do liczników od razu, a po synchronizacji
 * lokalny szkic ustępuje wersji serwerowej.
 */
function szkicHarmonogramu(pozycja: Pozycja, dzis: IsoDate): Platnosc[] {
  const od = pozycja.startsOn < dzis ? pozycja.startsOn : dzis
  const terminy = generateDueDates(
    {
      cadence: pozycja.cadence,
      dayOfMonth: pozycja.dayOfMonth,
      monthOfYear: pozycja.monthOfYear,
      dueDate: pozycja.dueDate,
      startsOn: pozycja.startsOn,
      endsOn: pozycja.endsOn,
    },
    od,
    addMonths(dzis, HORYZONT_MIESIECY),
  )

  return terminy.map((termin) => ({
    id: crypto.randomUUID(),
    itemId: pozycja.id,
    dueDate: termin,
    plannedAmountMinor: pozycja.amountMinor,
    plannedCurrency: pozycja.currency,
    status: 'planned' as const,
    actualAmountMinor: null,
    actualCurrency: null,
    fxRate: null,
    amountPlnMinor: null,
    paidAt: null,
    paidBy: null,
    notes: null,
    createdAt: pozycja.createdAt,
    updatedAt: pozycja.updatedAt,
    deletedAt: null,
  }))
}

export async function utworzPozycje(dane: DanePozycji): Promise<string> {
  const id = crypto.randomUUID()
  const znacznik = teraz()
  const pozycja: Pozycja = {
    ...dane,
    id,
    createdBy: biezacyIdUzytkownika(),
    createdAt: znacznik,
    updatedAt: znacznik,
    deletedAt: null,
  }

  const szkic = szkicHarmonogramu(pozycja, todayIso())
  await db.items.put(pozycja)
  await db.payments.bulkPut(szkic)

  await zakolejkuj({
    metoda: 'POST',
    sciezka: '/api/items',
    body: { id, ...dane },
    cofniecie: [
      { tabela: 'items', id, rekord: null },
      ...szkic.map((platnosc) => ({
        tabela: 'payments' as const,
        id: platnosc.id,
        rekord: null,
      })),
    ],
    // Sama pozycja wraca na miejsce po pobraniu; szkicu nie nakładamy — serwer
    // przyśle własny harmonogram.
    skutek: [{ tabela: 'items', id, rekord: pozycja }],
    szkicPlatnosci: szkic.map((platnosc) => platnosc.id),
  })

  return id
}

export async function zmienPozycje(id: string, zmiany: Partial<DanePozycji>): Promise<void> {
  const przed = await db.items.get(id)
  if (przed === undefined) throw new Error('Nie znaleziono pozycji')

  const po: Pozycja = { ...przed, ...zmiany, updatedAt: teraz() }
  await db.items.put(po)

  // Zmiana kwoty dotyka wyłącznie płatności planowanych — zapłacone niosą historię.
  const planowane = await db.payments.where('itemId').equals(id).toArray()
  const doZmiany = planowane.filter((platnosc) => platnosc.status === 'planned')
  await db.payments.bulkPut(
    doZmiany.map((platnosc) => ({
      ...platnosc,
      plannedAmountMinor: po.amountMinor,
      plannedCurrency: po.currency,
      updatedAt: teraz(),
    })),
  )

  await zakolejkuj({
    metoda: 'PATCH',
    sciezka: `/api/items/${id}`,
    body: zmiany,
    cofniecie: [
      { tabela: 'items', id, rekord: przed },
      ...doZmiany.map((platnosc) => ({
        tabela: 'payments' as const,
        id: platnosc.id,
        rekord: platnosc,
      })),
    ],
    skutek: [{ tabela: 'items', id, rekord: po }],
  })
}

export async function usunPozycje(id: string): Promise<void> {
  const przed = await db.items.get(id)
  const platnosci = await db.payments.where('itemId').equals(id).toArray()
  const planowane = platnosci.filter((platnosc) => platnosc.status === 'planned')

  await db.items.delete(id)
  await db.payments.bulkDelete(planowane.map((platnosc) => platnosc.id))

  await zakolejkuj({
    metoda: 'DELETE',
    sciezka: `/api/items/${id}`,
    cofniecie: [
      { tabela: 'items', id, rekord: przed ?? null },
      ...planowane.map((platnosc) => ({
        tabela: 'payments' as const,
        id: platnosc.id,
        rekord: platnosc,
      })),
    ],
    skutek: [
      { tabela: 'items', id, rekord: null },
      ...planowane.map((platnosc) => ({
        tabela: 'payments' as const,
        id: platnosc.id,
        rekord: null,
      })),
    ],
  })
}

type NazwaSlownika = 'categories' | 'vendors' | 'clients'
type RekordSlownika = Kategoria | Dostawca | Klient

const SCIEZKI: Record<NazwaSlownika, string> = {
  categories: '/api/categories',
  vendors: '/api/vendors',
  clients: '/api/clients',
}

function tabelaSlownika(slownik: NazwaSlownika) {
  return db[slownik] as unknown as {
    get(id: string): Promise<RekordSlownika | undefined>
    put(rekord: RekordSlownika): Promise<unknown>
    delete(id: string): Promise<unknown>
  }
}

export async function utworzWpisSlownika(
  slownik: NazwaSlownika,
  dane: Record<string, unknown>,
): Promise<string> {
  const id = crypto.randomUUID()
  const znacznik = teraz()
  const rekord = {
    id,
    ...dane,
    createdAt: znacznik,
    updatedAt: znacznik,
    deletedAt: null,
  } as unknown as RekordSlownika
  await tabelaSlownika(slownik).put(rekord)

  await zakolejkuj({
    metoda: 'POST',
    sciezka: SCIEZKI[slownik],
    body: { id, ...dane },
    cofniecie: [{ tabela: slownik, id, rekord: null }],
    skutek: [{ tabela: slownik, id, rekord }],
  })
  return id
}

export async function zmienWpisSlownika(
  slownik: NazwaSlownika,
  id: string,
  zmiany: Record<string, unknown>,
): Promise<void> {
  const cel = tabelaSlownika(slownik)
  const przed = await cel.get(id)
  if (przed === undefined) throw new Error('Nie znaleziono wpisu')

  const po = { ...przed, ...zmiany, updatedAt: teraz() } as RekordSlownika
  await cel.put(po)
  await zakolejkuj({
    metoda: 'PATCH',
    sciezka: `${SCIEZKI[slownik]}/${id}`,
    body: zmiany,
    cofniecie: [{ tabela: slownik, id, rekord: przed }],
    skutek: [{ tabela: slownik, id, rekord: po }],
  })
}

export async function usunWpisSlownika(slownik: NazwaSlownika, id: string): Promise<void> {
  const cel = tabelaSlownika(slownik)
  const przed = await cel.get(id)
  await cel.delete(id)

  await zakolejkuj({
    metoda: 'DELETE',
    sciezka: `${SCIEZKI[slownik]}/${id}`,
    cofniecie: [{ tabela: slownik, id, rekord: przed ?? null }],
    skutek: [{ tabela: slownik, id, rekord: null }],
  })
}
